import { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers';
import { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies';

import { CookiesName, ZmyleErrorId } from 'utils/constants';
import { formatDateAsZmyleApi } from 'utils/helpers';
import { ClientConfig } from 'services/clientConfig';
import { ServerConfig } from 'server/services/serverConfig';

interface ValidateGiftcardParams {
  network: AdaptedNetwork | null;
  giftcard: AdaptedGiftcard;
}

export const validateGiftcard = (params: ValidateGiftcardParams) => {
  const { network, giftcard } = params;

  const notValidError: ZmyleError = {
    errorId: ZmyleErrorId.NO_SUCH_GIFTCARD,
    errorMsg: `Unknown giftcard: '${giftcard.id}'`,
    occurredAt: formatDateAsZmyleApi(new Date()),
    status: 404,
  };

  if (!network) throw notValidError;

  const { purchasedForNetworkId, purchasedForRetailerId } = giftcard;

  if (network.networkType === 'COMMUNITY') {
    if (purchasedForNetworkId === network.id) return true;

    const { hasSuperpromotedGiftcards } = network.layoutMetadata;

    if (purchasedForRetailerId && hasSuperpromotedGiftcards) return true;

    throw notValidError;
  }

  if (purchasedForRetailerId && !purchasedForNetworkId) return true;

  throw notValidError;
};

export const getZmyleAuthTokenFromCookies = (
  cookies: ReadonlyRequestCookies,
) => {
  const authToken = cookies.get(CookiesName.authToken);

  if (!authToken) return '';

  return `${CookiesName.authToken}=${authToken.value}`;
};

const zmyleWidgetCookieCache = {
  cacheTime: 1000 * 60 * 60 * 24 * 2, // 2 days
  lastUpdate: Date.now(),
};

export const getZmyleWidgetCookieVersion = () => {
  if (!ClientConfig.getInstance().getConfig().isProduction) return Date.now();

  const now = Date.now();

  const shouldRevalidate =
    now - zmyleWidgetCookieCache.lastUpdate > zmyleWidgetCookieCache.cacheTime;

  if (shouldRevalidate) zmyleWidgetCookieCache.lastUpdate = now;

  return zmyleWidgetCookieCache.lastUpdate;
};

export const getHostname = (headers: ReadonlyHeaders) => {
  const host = headers.get('host');

  if (!host) return '';

  const serverConfig = ServerConfig.getInstance().getConfig();

  if (serverConfig.dev.networkHostname) return serverConfig.dev.networkHostname;

  const isLocalhost = /^localhost(:\d+)?$/.test(host);

  const protocol = isLocalhost ? 'http:' : 'https:';

  return `${protocol}//${host}`;
};

export const normalizeGiftcardId = (giftcardId: string) => {
  const matchedGiftcardId = giftcardId.match(/^([A-Za-z0-9]){16,32}$/g);

  if (!matchedGiftcardId) return null;

  const [normalizedGiftcardId] = matchedGiftcardId;

  if (!normalizedGiftcardId) return null;

  return normalizedGiftcardId;
};
